/**
 * @file   src\containers\Scheduler\ManageProfile\EditProfile.tsx
 * @brief  Scheduler Edit Profile page.
 * @date   Aug, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import React from 'react';
import { Col, Row, Link, Button, useState, useNavigate, useEffect, Modal } from '../../../components/ThirdPartyComponents';
import Strings from '../../../assests/strings/Strings.json';
import Arrow from '../../../assests/icons/DownArrow';
import KKIInput from '../../../components/KKIInput';
import KKIInputMask from '../../../components/KKIInputMask';
import KKICheckbox from '../../../components/KKICheckbox';
import { AdminRoutePath } from '../../../utils/enums';
import { IProfileUpdateRequest } from '../../../interfaces/userInterface';
import { validateForm } from '../../../utils/validationHelper';
import { PROFILE_SETUP_SCHEMA } from '../../../validations/userSchema';
import { updateAdminProfile, getAdminProfile, revertAllUserState, sendProfileEditOtp } from '../../../store/actions/userAction';
import { IObject } from '../../../interfaces/generalInterface';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { RootState } from '../../../store';
import { revertAllCommonState, verifyOtp } from '../../../store/actions/commonAction';
import { HTTP_STATUS_200, NUMBER_10, PHONE_COUNTRY_CODE, NUMBER_50, NUMBER_150 } from '../../../utils/constants';
import { MessageToaster } from '../../../utils/toastUtils';
import VerifyOtpForm from '../../../components/VerifyMobileModal';
import Loader from '../../../components/Loader';

// Toast object creation.
const toast = new MessageToaster();

const EditProfile = () => {
  // Declare action dispatch.
  const dispatch = useAppDispatch();

  // Navigate object creation.
  const navigate = useNavigate();

  // Access redux state variables.
  const {
    profileInfo,
    isProfileEditOtpSuccess,
    profileEditOtpErrorCode,
    profileEditOtpMessage,
    isAdminProfileFetchLoading,
    isAdminProfileUpdateSuccess,
    adminProfileUpdateErrorCode,
    adminProfileUpdateMessage,
  } = useAppSelector((state: RootState) => state.user);
  const { isVerifyOtpLoading, isVerifyOtpSuccess, verifyOtpErrorCode, verifyOtpMessage } = useAppSelector((state: RootState) => state.common);

  // Initialize component state variables.
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [mobile, setMobile] = useState<string>('');
  const [currentMobile, setCurrentMobile] = useState<string>('');
  const [isVolunteer, setIsVolunteer] = useState<boolean>(false);
  const [errorFields, setErrorFields] = useState<IObject | null>(null);
  const [showVerifyMobileModal, setShowVerifyMobileModal] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [showOtpTimer, setOtpTimerStatus] = useState<boolean>(false);
  const [subscribed, setSubscribed] = useState<boolean>(false);

  // Component initial loading sideEffect to fetch profile api.
  useEffect(() => {
    dispatch(getAdminProfile());
    return () => {
      dispatch(revertAllUserState());
      dispatch(revertAllCommonState());
    };
  }, []);

  // Profile api response state change side effect handler.
  useEffect(() => {
    if (profileInfo) {
      const phone = profileInfo?.phone ? profileInfo?.phone.substring(profileInfo.phone.length - NUMBER_10) : '';
      setFirstName(profileInfo?.firstName || '');
      setLastName(profileInfo?.lastName || '');
      setEmail(profileInfo?.email || '');
      setMobile(phone);
      setCurrentMobile(phone);
      setIsVolunteer(profileInfo?.volunteer);
      setSubscribed(profileInfo?.isSubscribed || false);
    }
  }, [profileInfo]);

  // Otp send api response state side effect handling.
  useEffect(() => {
    if (isProfileEditOtpSuccess && profileEditOtpErrorCode === HTTP_STATUS_200 && profileEditOtpMessage) {
      setShowVerifyMobileModal(true);
      setOtpTimerStatus(true);
      toast.toastSuccess(profileEditOtpMessage);
    } else if (!isProfileEditOtpSuccess && profileEditOtpErrorCode > HTTP_STATUS_200 && profileEditOtpMessage) {
      toast.toastError(profileEditOtpMessage);
    }
  }, [isProfileEditOtpSuccess, profileEditOtpErrorCode, profileEditOtpMessage]);

  // Otp verify api response state side effect handling.
  useEffect(() => {
    if (isVerifyOtpSuccess && verifyOtpErrorCode === HTTP_STATUS_200 && verifyOtpMessage) {
      toast.toastSuccess(verifyOtpMessage);
      setShowVerifyMobileModal(false);
      setIsVerified(true);
    } else if (!isVerifyOtpSuccess && verifyOtpErrorCode > HTTP_STATUS_200 && verifyOtpMessage) {
      toast.toastError(verifyOtpMessage);
    }
  }, [isVerifyOtpSuccess, verifyOtpErrorCode, verifyOtpMessage]);

  // Update profile details api response state side effect handling.
  useEffect(() => {
    if (isAdminProfileUpdateSuccess && adminProfileUpdateErrorCode === HTTP_STATUS_200 && adminProfileUpdateMessage) {
      toast.toastSuccess(adminProfileUpdateMessage);
      setErrorFields(null);
      navigate(AdminRoutePath.SCHManageProfile);
    } else if (!isAdminProfileUpdateSuccess && adminProfileUpdateErrorCode > HTTP_STATUS_200 && adminProfileUpdateMessage) {
      toast.toastError(adminProfileUpdateMessage);
    }
  }, [isAdminProfileUpdateSuccess, adminProfileUpdateErrorCode, adminProfileUpdateMessage]);

  // Input first name element change event handler.
  const onFirstNameInputChange = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    const profileSetupForm = {
      firstName: value,
    };

    const errorResult = await validateForm(profileSetupForm, PROFILE_SETUP_SCHEMA, errorFields);
    setErrorFields(errorResult);
    setFirstName(value);
  };

  // Input last name element change event handler.
  const onLastNameInputChange = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    const profileSetupForm = {
      lastName: value,
    };

    const errorResult = await validateForm(profileSetupForm, PROFILE_SETUP_SCHEMA, errorFields);
    setErrorFields(errorResult);
    setLastName(value);
  };

  // Input email element change event handler.
  const onEmailInputChange = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    const profileSetupForm = {
      email: value,
    };

    const errorResult = await validateForm(profileSetupForm, PROFILE_SETUP_SCHEMA, errorFields);
    setErrorFields(errorResult);
    setEmail(value);
  };

  // Input mobile element change event handler.
  const onMobileInputChange = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    const inputVal = value ? value.replace(/\D/g, '') : '';
    const profileSetupForm = {
      mobile: inputVal,
    };

    const errorResult = await validateForm(profileSetupForm, PROFILE_SETUP_SCHEMA, errorFields);
    setErrorFields(errorResult);
    setMobile(inputVal);
  };

  // Subscription checkbox change event handler.
  const onSubscriptionCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;
    setSubscribed(name === Strings.SetupProfile.Subscribed.toLocaleLowerCase());
  };

  // Otp verification submission event.
  const onVerifyOtpSubmit = (otp: string) => {
    dispatch(verifyOtp({ otp, phone: `${PHONE_COUNTRY_CODE}${mobile}` }));
  };

  // Otp resend method.
  const onResend = () => dispatch(sendProfileEditOtp({ phone: `${PHONE_COUNTRY_CODE}${mobile}`, isResend: true }));

  // Edit profile form submit button event handler
  const onSubmit = async (evt: React.MouseEvent<HTMLElement>) => {
    evt.preventDefault();
    const profileSetupForm = {
      firstName,
      lastName,
      email,
      mobile,
    };

    const errorResult = await validateForm(profileSetupForm, PROFILE_SETUP_SCHEMA, errorFields);
    setErrorFields(errorResult);
    if (Object.keys(errorResult).length === 0) {
      if (currentMobile !== mobile && !isVerified) {
        dispatch(sendProfileEditOtp({ phone: `${PHONE_COUNTRY_CODE}${mobile}` }));
      } else {
        const profileUpdateRequest: IProfileUpdateRequest = {
          firstName,
          lastName,
          email,
          phone: `${PHONE_COUNTRY_CODE}${mobile}`,
          volunteer: isVolunteer,
          isSubscribed: subscribed,
        };
        dispatch(updateAdminProfile(profileUpdateRequest));
      }
    }
  };

  return (
    <div>
      <div className="pageheader d-flex justify-content-between">
        <h1>
          <Link to={AdminRoutePath.SCHManageProfile} className="back-btn">
            <Arrow />
          </Link>
          {Strings.HD.EditProfile}
        </h1>
      </div>
      <div className="profile-main">
        <Row>
          <Col xl={6} lg={6} sm={12} className="profile-edit">
            <KKIInput
              id="firstname"
              label={Strings.SetupProfile.FirstName}
              name="firstName"
              type="text"
              value={firstName}
              onChange={onFirstNameInputChange}
              alert={errorFields?.firstName || ''}
              maxLength={NUMBER_50}
            />
          </Col>
          <Col xl={6} lg={6} sm={12} className="profile-edit">
            <KKIInput
              id="lastname"
              label={Strings.SetupProfile.LastName}
              name="lastName"
              type="text"
              value={lastName}
              onChange={onLastNameInputChange}
              alert={errorFields?.lastName || ''}
              maxLength={NUMBER_50}
            />
          </Col>
          <Col xl={6} lg={6} sm={12} className="profile-edit">
            <KKIInput
              id="email"
              label={Strings.SetupProfile.Email}
              name="email"
              type="text"
              value={email}
              onChange={onEmailInputChange}
              alert={errorFields?.email || ''}
              maxLength={NUMBER_150}
            />
          </Col>
          <Col xl={6} lg={6} sm={12} className="profile-edit">
            <KKIInputMask
              id="mobnumber"
              label={Strings.SetupProfile.MobileNumber}
              name="mobile"
              type="text"
              mask="999-999-9999"
              value={mobile}
              placeholder={Strings.CsLogin.MobileNumber}
              onChange={onMobileInputChange}
              alert={errorFields?.mobile || ''}
              verified={isVerified}
            />
          </Col>
          <Col xl={6} lg={6} sm={12} className="profile-edit">
            <label className="form-label" htmlFor="subscribe">
              {Strings.SetupProfile.ReceiveSMS}
            </label>
            <Col id="subscribe">
              <KKICheckbox
                id="signup"
                name="subscribed"
                checkBoxType="radio"
                label={Strings.SetupProfile.Subscribe}
                inline
                checked={subscribed}
                onChange={onSubscriptionCheckChange}
              />
              <KKICheckbox
                id="signup"
                name="unsubscribed"
                checkBoxType="radio"
                label={Strings.SetupProfile.unSubscribe}
                inline
                checked={!subscribed}
                onChange={onSubscriptionCheckChange}
              />
            </Col>
          </Col>
        </Row>
        <Col className="my-3">
          <Button variant="primary" onClick={onSubmit}>
            {Strings.Button.Submit}
          </Button>
        </Col>
      </div>
      <Modal show={showVerifyMobileModal} onHide={() => setShowVerifyMobileModal(false)} centered className="verify-mob-modal" backdrop="static">
        <Modal.Header closeButton className="bg-transparent" />
        <VerifyOtpForm onSubmit={(otp: string) => onVerifyOtpSubmit(otp)} onResend={onResend} showOtpTimer={showOtpTimer} setTimerStatus={(val) => setOtpTimerStatus(val)} />
      </Modal>
      {(isAdminProfileFetchLoading || isVerifyOtpLoading) && <Loader />}
    </div>
  );
};
export default EditProfile;
